import {
  Form,
  DatePicker,
  InputNumber,
  Input,
  Select,
  Upload,
  message,
  Button,
} from "antd";
import { useEffect, useState } from "react";
import { InboxOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";

const { TextArea } = Input;
const { Dragger } = Upload;

const CustomOrderForm = ({
  form,
  eventTypeOptions,
  customPackageTypeOptions,
  packageTypesLoading,
  eventTypesLoading,
  serviceTypeOptions,
  serviceTypesLoading,
  dietaryPreferenceOptions,
  dietaryPrefsLoading,
  beverageTypeOptions,
  beverageTypesLoading,
}) => {
  const [fileList, setFileList] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ✅ Upload PDF to Cloudinary

  // ✅ Handle PDF upload

  const handlePdfUpload = async (file) => {
    try {
      setUploading(true);
      message.loading({ content: "Uploading PDF...", key: "pdfUpload" });

      const url = await uploadToCloudinary(file);

      setPdfUrl(url);
      form.setFieldValue("quoteUrl", url);

      message.success({
        content: "PDF uploaded successfully!",
        key: "pdfUpload",
      });

      return url;
    } catch (error) {
      console.error("PDF upload failed:", error);
      message.error({
        content: "Failed to upload PDF. Please try again.",
        key: "pdfUpload",
      });
      throw error;
    } finally {
      setUploading(false);
    }
  };
  // Calculate total budget
  const guestCount = Form.useWatch("guestCount", form);
  const budgetPerPerson = Form.useWatch("budgetPerPerson", form);

  useEffect(() => {
    if (guestCount && budgetPerPerson) {
      const total = parseFloat(guestCount) * parseFloat(budgetPerPerson);
      form.setFieldValue("totalBudget", total);
    } else {
      form.setFieldValue("totalBudget", 0);
    }
  }, [guestCount, budgetPerPerson, form]);

  // PDF Upload configuration
  const uploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1, // Changed to 1, as your logic only handles a single PDF
    accept: ".pdf",
    fileList: fileList,
    beforeUpload: async (file) => {
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        message.error("You can only upload PDF files!");
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        // <--- CORRECTED: Was isLt10M, now is isLt5M
        message.error("PDF must be smaller than 5MB!");
        return Upload.LIST_IGNORE;
      }

      // Inside beforeUpload
      try {
        const uploadedUrl = await handlePdfUpload(file); // Get the URL directly
        // Update fileList only on successful upload
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            url: uploadedUrl, // <--- CORRECTED: Use uploadedUrl here
          },
        ]);
        // No need to set pdfUrl here, as handlePdfUpload already does it.
        // It's crucial for the component's preview logic that pdfUrl is set.
        // If handlePdfUpload sets it, it will eventually re-render.
        // For immediate feedback in this scope, uploadedUrl is direct.
      } catch (error) {
        // handlePdfUpload already shows an error message
        // If an error occurs, ensure file is not added to the list.
        return Upload.LIST_IGNORE;
      }

      // Prevent default upload behavior
      return false;
    },
    onRemove: (file) => {
      // Added 'file' parameter to onRemove (good practice, though not strictly used here)
      setFileList([]);
      setPdfUrl(null);
      form.setFieldValue("quoteUrl", null);
      message.info("PDF removed."); // Optional: provide feedback
    },
    // Adding custom button for file list item (optional, but good for custom display)
    itemRender: (originNode, file, fileList, actions) => {
      // When maxCount is 1, you might not strictly need this,
      // but it gives you full control over how the uploaded file is displayed
      // within the Ant Design Upload's internal list if you were to show it.
      // Your custom PDF Preview section below the Dragger effectively replaces this.
      return null; // Don't show Ant Design's default list item if you have your own display
    },
  };

  // Preview PDF in new tab
  const handlePreview = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <Form form={form} layout="vertical">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="When is the event?"
            name="eventDate"
            rules={[
              { required: true, message: "Please select the event date" },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder="Select date and time"
              className="w-full"
              size="large"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>

          <Form.Item
            label="What type of event are you planning?"
            name="eventType"
            rules={[{ required: true, message: "Please select an event type" }]}
          >
            <Select
              placeholder="Select event type"
              size="large"
              loading={eventTypesLoading}
              options={eventTypeOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Could you provide more details about the event?"
            name="eventDetails"
            rules={[{ required: true, message: "Please enter event details" }]}
          >
            <Input
              placeholder="Annual company meeting with presentations and awards."
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="Contact Person's Phone Number"
            name="contactPerson"
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^[0-9 +]{11,14}$/,
                message: "Only numbers allowed. Must be 11-14 digits",
              },
            ]}
          >
            <Input
              placeholder="08123456789"
              className="w-full"
              size="large"
              maxLength={14}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="How many guests are you expecting?"
            name="guestCount"
            rules={[
              { required: true, message: "Please enter guest count" },
              { type: "number", min: 1, message: "Must be at least 1" },
            ]}
          >
            <InputNumber
              placeholder="720"
              className="w-full"
              size="large"
              min={1}
            />
          </Form.Item>

          <Form.Item
            label="How would you want us to provide the catering service?"
            name="serviceType"
            rules={[
              { required: true, message: "Please select a service type" },
            ]}
          >
            <Select
              placeholder="Select service type"
              size="large"
              loading={serviceTypesLoading}
              options={serviceTypeOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Venue/ Delivery Location"
            name="venue"
            rules={[
              { required: true, message: "Please enter the venue location" },
            ]}
          >
            <Input placeholder="12 Adewale Street, Lagos" size="large" />
          </Form.Item>

          <Form.Item
            label="Any Noteworthy Dietary Requirements"
            name="dietaryRequirements"
            rules={[
              { required: true, message: "Please select dietary requirements" },
            ]}
          >
            <Select
              placeholder="Select dietary preference"
              size="large"
              loading={dietaryPrefsLoading}
              options={dietaryPreferenceOptions}
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Drinks"
            name="drinks"
            rules={[{ required: true, message: "Please select beverage type" }]}
          >
            <Select
              placeholder="Select beverage type"
              size="large"
              loading={beverageTypesLoading}
              options={beverageTypeOptions}
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Package type"
            name="packagingType"
            rules={[
              { required: true, message: "Please select a package type" },
            ]}
          >
            <Select
              placeholder="Select package type"
              size="large"
              loading={packageTypesLoading}
              options={customPackageTypeOptions}
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Customer's Role"
            name="customerRole"
            rules={[
              { required: true, message: "Please enter the customer's role" },
            ]}
          >
            <Input placeholder="Celebrant" size="large" />
          </Form.Item>

          <Form.Item
            label="Budget (per person)"
            name="budgetPerPerson"
            rules={[
              { required: true, message: "Please enter budget per person" },
              { type: "number", min: 0, message: "Must be a positive number" },
            ]}
          >
            <InputNumber
              placeholder="6000"
              className="w-full"
              size="large"
              min={0}
              formatter={(value) =>
                `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/₦\s?|(,*)/g, "")}
            />
          </Form.Item>
        </div>

        <Form.Item
          label={
            <span className="text-sm font-medium">
              Total Budget{" "}
              <span className="text-gray-500 font-normal">
                (Auto-calculated: Guest Count × Budget Per Person)
              </span>
            </span>
          }
          name="totalBudget"
        >
          <Input
            readOnly
            size="large"
            className="bg-gray-50"
            value={
              form.getFieldValue("totalBudget")
                ? `₦${parseFloat(
                    form.getFieldValue("totalBudget") || 0
                  ).toLocaleString()}`
                : "₦0"
            }
            formatter={(value) =>
              `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/₦\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item
          label="Food Request"
          name="foodRequest"
          rules={[
            { required: true, message: "Please enter your food request" },
          ]}
        >
          <TextArea
            placeholder="Order List:&#10;• Jollof Rice – 2 litres (feeds: 3 people on a meal)&#10;• Pounded Yam – 1.5 kg (about 5 servings)&#10;• Egusi Soup – 1 litre (pairs with the pounded yam)&#10;• Cupcakes – 12 pieces (mix of chocolate & vanilla)&#10;• Chapman Drinks – 3 litres (about 9 servings)"
            rows={6}
            size="large"
          />
        </Form.Item>
        <Form.Item
          label="Quote Amount"
          name="quoteAmount"
          rules={[{ required: true, message: "Please enter the quote amount" }]}
        >
          <InputNumber
            placeholder="500000"
            className="w-full"
            size="large"
            min={0}
            formatter={(value) =>
              `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/₦\s?|(,*)/g, "")}
          />
        </Form.Item>
        <Form.Item
          label="Upload Quote (PDF)"
          name="quoteUrl"
          rules={[{ required: true, message: "Please upload a quote PDF" }]}
        >
          <div className="space-y-3">
            <Dragger
              {...uploadProps}
              className="hover:border-orange-500"
              disabled={uploading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined
                  className="text-orange-500"
                  style={{ fontSize: 48 }}
                />
              </p>
              <p className="ant-upload-text text-base font-medium">
                {uploading
                  ? "Uploading PDF..."
                  : "Click or drag PDF file to this area to upload"}
              </p>
              <p className="ant-upload-hint text-sm text-gray-500">
                Support for a single PDF upload. Maximum file size: 5MB
              </p>
            </Dragger>

            {/* PDF Preview Section */}
            {pdfUrl && (
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {fileList[0]?.name || "Quote PDF"}
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF uploaded successfully
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="default"
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    size="small"
                  >
                    Preview
                  </Button>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setFileList([]);
                      setPdfUrl(null);
                      form.setFieldValue("quoteUrl", null);
                    }}
                    size="small"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CustomOrderForm;
